import { FieldKey } from '../../context/field-keys.js';
import { defaultRound } from '../../shared/round.js';
/**
 * Lease rent <-> area relations, extracted from the `multipleListenerToRatesFactory` /
 * `singleListenerToRatesFactory` wirings in common-listener-helper.ts:
 *
 *   rentPerSqm           = baseRentYearly / areaLeased        (addListenerToRentPerSQM)
 *   arrangementTotal     = baseRentYearly * areaLeased        (same listener, secondary output)
 *   baseRentYearly       = rentPerSqm * areaLeased            (addListenerToBaseRentYearly)
 *   baseRentYearly       = arrangementTotal / areaLeased      (addListenerToLeaseArrangementTotalValue)
 *
 * Division by a missing/zero area yields null (the original `divideValues` returns null when the
 * divisor is falsy). Rounding is delegated to `deps.round`. The cross-field listener orchestration
 * (which direction fires, AUS asking-rate-type gate) stays in the Phase 4 listener.
 */
const num = v => v == null ? null : Number(v);
/** rentPerSqm = baseRentYearly / areaLeased */
export const rentPerSqmFromYearlyAreaRule = {
  id: 'lease.rent-per-sqm.from-yearly-area',
  inputs: [FieldKey.BaseRentYearly, FieldKey.AreaLeased],
  outputs: [FieldKey.RentPerSqm],
  formulaVersion: '1.0.0',
  run: (ctx, deps) => {
    const round = deps?.round ?? defaultRound;
    const yearly = num(ctx.get(FieldKey.BaseRentYearly));
    const area = num(ctx.get(FieldKey.AreaLeased));
    ctx.set(FieldKey.RentPerSqm, yearly == null || !area ? null : round(yearly / area, FieldKey.RentPerSqm));
  }
};
/** arrangementTotal = baseRentYearly * areaLeased */
export const arrangementTotalFromYearlyAreaRule = {
  id: 'lease.arrangement-total.from-yearly-area',
  inputs: [FieldKey.BaseRentYearly, FieldKey.AreaLeased],
  outputs: [FieldKey.LeaseArrangementTotalValue],
  formulaVersion: '1.0.0',
  run: (ctx, deps) => {
    const round = deps?.round ?? defaultRound;
    const yearly = num(ctx.get(FieldKey.BaseRentYearly));
    const area = num(ctx.get(FieldKey.AreaLeased));
    ctx.set(FieldKey.LeaseArrangementTotalValue, yearly == null || area == null ? null : round(yearly * area, FieldKey.LeaseArrangementTotalValue));
  }
};
/** baseRentYearly = rentPerSqm * areaLeased */
export const yearlyFromRentPerSqmAreaRule = {
  id: 'lease.base-rent-yearly.from-rent-per-sqm-area',
  inputs: [FieldKey.RentPerSqm, FieldKey.AreaLeased],
  outputs: [FieldKey.BaseRentYearly],
  formulaVersion: '1.0.0',
  run: (ctx, deps) => {
    const round = deps?.round ?? defaultRound;
    const rentPerSqm = num(ctx.get(FieldKey.RentPerSqm));
    const area = num(ctx.get(FieldKey.AreaLeased));
    ctx.set(FieldKey.BaseRentYearly, rentPerSqm == null || area == null ? null : round(rentPerSqm * area, FieldKey.BaseRentYearly));
  }
};
/** baseRentYearly = arrangementTotal / areaLeased */
export const yearlyFromArrangementTotalAreaRule = {
  id: 'lease.base-rent-yearly.from-arrangement-total-area',
  inputs: [FieldKey.LeaseArrangementTotalValue, FieldKey.AreaLeased],
  outputs: [FieldKey.BaseRentYearly],
  formulaVersion: '1.0.0',
  run: (ctx, deps) => {
    const round = deps?.round ?? defaultRound;
    const total = num(ctx.get(FieldKey.LeaseArrangementTotalValue));
    const area = num(ctx.get(FieldKey.AreaLeased));
    ctx.set(FieldKey.BaseRentYearly, total == null || !area ? null : round(total / area, FieldKey.BaseRentYearly));
  }
};
export const leaseRentRateRules = [rentPerSqmFromYearlyAreaRule, arrangementTotalFromYearlyAreaRule, yearlyFromRentPerSqmAreaRule, yearlyFromArrangementTotalAreaRule];
